import qs from 'qs';
import { apiGet, apiPost } from './client';
import type {
  AgentDefinition,
  AgentOverviewBundle,
  ExecutionResult,
  OperationResult,
  PagedItemsResult,
} from './types';

// Pattern: every search/list endpoint on RestApi.Tenant returns
// OperationResult<PagedItemsResult<T>>. apiGet returns the unwrapped JSON
// response (camelCase-normalized via the axios interceptor). The list
// helpers below all unwrap to PagedItemsResult so callers can access .data
// (the items array) and .paging without dealing with the OperationResult shell.

// Wraps GET /api/Agent/Search?merchantId=X&search=...&pageNo=...&pageSize=...
// Returns the paged list of agents for a merchant. includeGlobal=true also
// includes "global" agents (MerchantId IS NULL).

export interface AgentSearchParams {
  merchantId: number;
  /** Matches the display name, canonical name, A2A name, description or id; never the instruction body. */
  search?: string;
  pageNo?: number;
  pageSize?: number;
  includeGlobal?: boolean;
  /** Server-side filters (AIAgentConfigManager.SearchDirectoryAsync); omitted means no filter. */
  audience?: 'patient' | 'care' | 'both' | 'none';
  /** The lifecycle (OO.Domain.AIInfo.AgentLifecycleStatus): draft also matches never given a status. */
  status?: 'active' | 'draft' | 'inactive' | 'archived';
  router?: 'on' | 'off';
  consults?: 'on' | 'off';
  categoryId?: number;
  /** Only these agents (a short list someone holds, e.g. their pinned or recent agents). */
  ids?: number[];
  /** Never these (a link picker does not offer the agent itself or one already linked). */
  excludeIds?: number[];
  /** Unset: the practice's display order. Every order ends with the id, so pages never overlap. */
  sort?: 'name' | 'category';
  descending?: boolean;
}

export async function searchAgents(params: AgentSearchParams): Promise<PagedItemsResult<AgentDefinition>> {
  const {
    merchantId, search = '', pageNo = 1, pageSize = 50, includeGlobal = false,
    audience, status, router, consults, categoryId, ids, excludeIds, sort, descending,
  } = params;
  // The paged list comes wrapped in OperationResult; a refused filter word comes back in its message.
  // Lists go as repeated keys (ids=1&ids=2), the form ASP.NET binds to long[].
  const wrapped = await apiGet<OperationResult<PagedItemsResult<AgentDefinition>>>(
    '/api/Agent/Search',
    { merchantId, search, pageNo, pageSize, includeGlobal, audience, status, router, consults, categoryId, ids, excludeIds, sort, descending },
    { paramsSerializer: { serialize: (p) => qs.stringify(p, { arrayFormat: 'repeat' }) } },
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to load agents');
  }
  return wrapped.data;
}

/**
 * The directory's rows for these ids, in the practice or shared by every practice: a light row (never the
 * instruction body), for a name, a label or a shelf. Ids the server does not know are simply absent.
 */
export async function getAgentsByIds(merchantId: number, ids: number[]): Promise<AgentDefinition[]> {
  const unique = [...new Set(ids)];
  if (unique.length === 0) return [];
  const page = await searchAgents({ merchantId, ids: unique, includeGlobal: true, pageSize: unique.length });
  return page.data ?? [];
}

/** One clash the save's identity check found: the field (`CanonicalName`, `A2AAgentName` or `DisplayName`), Save's words, the agents. */
export interface AgentIdentityClash {
  field: 'CanonicalName' | 'A2AAgentName' | 'DisplayName' | string | null;
  message: string;
  agentIds: number[];
}

/**
 * POST /api/Agent/CheckIdentity — the save's own identity check, asked before saving (AIAgentConfigManager.CheckIdentity):
 * every clash with every agent in the practice, in the words Save would use. Empty when the names are free.
 */
export async function checkAgentIdentity(candidate: Partial<AgentDefinition>): Promise<AgentIdentityClash[]> {
  const result = await apiPost<OperationResult<AgentIdentityClash[]>>('/api/Agent/CheckIdentity', candidate);
  if (!result.isSuccess) throw new Error(result.message || 'Could not check the names');
  return result.data ?? [];
}

/**
 * One recorded change of an agent's status (ai.AiAgentStatusChange, AgentLifecycleManager). `from` is null when the agent
 * had never been given a status. The evaluation fields are the counts the server held when the change was made: what
 * the decision looked at, not the agent's current state.
 */
export interface AgentStatusChange {
  id: number;
  agentId: number;
  from: 'active' | 'draft' | 'inactive' | 'archived' | null;
  to: 'active' | 'draft' | 'inactive' | 'archived';
  note?: string | null;
  changedById: number;
  changedBy?: string | null;
  changedUtc: string;
  evaluationRunId?: number | null;
  evaluationRunUtc?: string | null;
  libraryScenarios?: number | null;
  needsAttention?: number | null;
  evidenceIncomplete?: number | null;
  notAssessed?: number | null;
  passed?: number | null;
}

/**
 * POST /api/Agent/{id}/Status — changes the agent's status in the selected practice and records it, with the note and
 * the evaluation the server held. Throws with the server's words when it refuses (nothing changed).
 */
export async function changeAgentStatus(
  agentId: number,
  status: 'active' | 'draft' | 'inactive' | 'archived',
  note?: string,
): Promise<AgentStatusChange> {
  const result = await apiPost<OperationResult<AgentStatusChange>>(`/api/Agent/${agentId}/Status`, { status, note });
  if (!result.isSuccess || !result.data) throw new Error(result.message || 'The status was not changed');
  return result.data;
}

/** GET /api/Agent/{id}/StatusHistory — the agent's status changes, newest first. */
export async function getAgentStatusHistory(agentId: number): Promise<AgentStatusChange[]> {
  const result = await apiGet<OperationResult<AgentStatusChange[]>>(`/api/Agent/${agentId}/StatusHistory`);
  if (!result.isSuccess) throw new Error(result.message || 'Could not load the status history');
  return result.data ?? [];
}

export async function getAgentOverview(assistantId: number): Promise<AgentOverviewBundle> {
  const result = await apiGet<ExecutionResult<AgentOverviewBundle>>(
    `/api/Agent/Overview/${assistantId}`
  );
  if (!result.isSuccess || !result.data) {
    throw new Error(result.message || 'Failed to load agent overview');
  }
  return result.data;
}

export async function getAgentById(assistantId: number): Promise<AgentDefinition> {
  const result = await apiGet<OperationResult<AgentDefinition>>(`/api/Agent/GetById/${assistantId}`);
  if (!result.isSuccess || !result.data) {
    throw new Error(result.message || `Agent ${assistantId} not found`);
  }
  return result.data;
}

// GET /api/Agent/Instructions/{id} — ONE agent's prompt body, and nothing else.
//
// api/Agent/Search stopped returning `instructions`: it is nvarchar(max), ~7 MB across
// the fleet, and returning it per row is what put that endpoint on a 30 s command
// timeout. Anything that needs the body asks for it one agent at a time.
//
// Narrower than getAgentById on purpose — an instruction editor needs the body, not the
// whole row — and it reads THROUGH to the database, so it cannot serve a stale body from
// an agent object that has been sitting in a store.
export async function getAgentInstructions(assistantId: number): Promise<string> {
  const result = await apiGet<OperationResult<{ id: number; instructions: string }>>(
    `/api/Agent/Instructions/${assistantId}`
  );
  if (!result.isSuccess || !result.data) {
    throw new Error(result.message || `Instructions for agent ${assistantId} could not be loaded`);
  }
  return result.data.instructions ?? '';
}

// POST /api/Agent/Update — full-model update. Backend's BaseCrudApiController.Update
// expects the entire AgentDefinition; partial updates aren't supported, so callers must
// pass the previously-loaded agent with the targeted field flipped.
//
// ⚠ That agent must come from getAgentById (or Overview), NOT from searchAgents — a list
// row carries no `instructions`. The server treats an absent body as "unchanged" rather
// than "clear it", so this cannot wipe the column; but a list-sourced save would still be
// relying on that guard instead of sending the truth.
export async function updateAgent(agent: AgentDefinition): Promise<AgentDefinition> {
  const result = await apiPost<OperationResult<AgentDefinition>>('/api/Agent/Update', agent);
  if (!result.isSuccess) {
    throw new Error(result.message || 'Failed to update agent');
  }
  return (result.data as AgentDefinition) ?? agent;
}

// ─────────────────────────────────────────────────────────────────────────
// Smart instruction editor — AI co-pilot for ai.AiAgent.Instructions
// ─────────────────────────────────────────────────────────────────────────

export type InstructionDraftMode =
  | 'tighten'
  | 'addSection'
  | 'rewriteSelection'
  | 'findRedundancy';

export interface InstructionDraftRequest {
  agentId: number;
  mode: InstructionDraftMode;
  /** Required for addSection / rewriteSelection. Free text. */
  userRequest?: string;
  /** Full current body (so the model edits the in-progress draft, not the saved DB row). */
  currentInstructions: string;
  /** rewriteSelection only — the verbatim substring to rewrite in place. */
  selection?: string;
}

export interface InstructionRedundancyHit {
  capabilityName?: string;
  overlappingSnippet?: string;
  recommendation?: string;
}

export interface InstructionDraftResponse {
  /** Full new body for tighten / addSection / rewriteSelection. Null for findRedundancy. */
  proposedInstructions?: string | null;
  rationale?: string;
  redundancyHits?: InstructionRedundancyHit[];
}

export async function draftInstruction(
  req: InstructionDraftRequest,
): Promise<InstructionDraftResponse> {
  const result = await apiPost<OperationResult<InstructionDraftResponse>>(
    '/api/Agent/InstructionDraft',
    req,
  );
  if (!result.isSuccess || !result.data) {
    throw new Error(result.message || 'AI drafting failed');
  }
  return result.data;
}

// ─────────────────────────────────────────────────────────────────────────
// AI co-pilot for "+ New agent" flow
// ─────────────────────────────────────────────────────────────────────────

export interface AgentDraftRequest {
  merchantId: number;
  userRequest: string;
}

export interface AgentDraftResponse {
  displayName?: string;
  canonicalName?: string;
  description?: string;
  audience?: string;
  patientApp?: boolean;
  merchantApp?: boolean;
  rationale?: string;
  similarAgentNames?: string[];
}

export async function draftAgent(req: AgentDraftRequest): Promise<AgentDraftResponse> {
  const result = await apiPost<OperationResult<AgentDraftResponse>>(
    '/api/Agent/AgentDraft',
    req,
  );
  if (!result.isSuccess || !result.data) {
    throw new Error(result.message || 'AI drafting failed');
  }
  return result.data;
}

/** Wrapper around the existing /Add endpoint, for AgentEditorDrawer's "Save". */
export async function addAgent(agent: Partial<AgentDefinition>): Promise<AgentDefinition> {
  const result = await apiPost<OperationResult<AgentDefinition>>('/api/Agent/Add', agent);
  if (!result.isSuccess || !result.data) {
    throw new Error(result.message || 'Failed to create agent');
  }
  return result.data;
}

// ─────────────────────────────────────────────────────────────────────────
// Send-to-tab — Agent Studio test prompt → patient/provider open session
// ─────────────────────────────────────────────────────────────────────────

export type TestPromptTargetApp = 'patient' | 'provider';

export interface SendTestPromptRequest {
  /** Logged-in user id in the target app (member id for patient, employee id for provider). */
  targetUserId: string;
  /** 1 = patient app, 2 = provider app. */
  systemId: 1 | 2;
  prompt: string;
  agentName?: string;
  agentDisplayName?: string;
}

/**
 * POST /api/Agent/SendTestPrompt — fans out via SignalR to the
 * target user's open patient or provider tab. Tab listens for
 * `testPromptInjected` and opens the chat with the prompt prefilled.
 */
export async function sendTestPromptToSession(req: SendTestPromptRequest): Promise<void> {
  // eslint-disable-next-line no-console
  console.log('[sendTestPromptToSession] →', {
    targetUserId: req.targetUserId,
    systemId: req.systemId,
    agentName: req.agentName,
    agentDisplayName: req.agentDisplayName,
    promptLen: req.prompt?.length,
  });
  const result = await apiPost<OperationResult<{ delivered: boolean }>>(
    '/api/Agent/SendTestPrompt',
    req,
  );
  // eslint-disable-next-line no-console
  console.log('[sendTestPromptToSession] ←', result);
  if (!result.isSuccess) throw new Error(result.message || 'Send failed');
}
